import { parseJwt } from '../../parseJwt';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaBell,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaDownload,
  FaMapMarkerAlt,
  FaPlay,
  FaRegClock,
  FaTools,
  FaUserCircle
} from 'react-icons/fa';
import api from '../Axios/axios';
import TechnicalServicePersonnelShell, { getUserInfoFromToken } from './TechnicalServicePersonnelShell';
import './TechnicalService.scss';

const STATUS_CODE_MAP = {
  New: 1,
  Assigned: 2,
  InProgress: 3,
  CompletedByPersonnel: 4,
  WaitingManagerApproval: 5,
  Approved: 6,
  SentToAccounting: 7,
  Cancelled: 8
};

const normalizeStatus = (status) => {
  if (typeof status === 'number') return status;
  if (typeof status === 'string') {
    if (STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 1 : parsed;
  }
  return 1;
};

const getUserIdFromToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return '';

  try {
    const decoded = parseJwt(token);
    return (
      decoded.sub ||
      decoded.nameid ||
      decoded.userId ||
      decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
      ''
    );
  } catch (error) {
    return '';
  }
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const formatTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const formatDayMonth = (value) =>
  new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: 'long',
    timeZone: 'Europe/Istanbul'
  }).format(value);

const formatWeekdayShort = (value) =>
  new Intl.DateTimeFormat('tr-TR', {
    weekday: 'short',
    timeZone: 'Europe/Istanbul'
  }).format(value);

const getWeekStart = (date) => {
  const current = new Date(date);
  const day = current.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  current.setDate(current.getDate() + diff);
  current.setHours(0, 0, 0, 0);
  return current;
};

const getJobId = (job) => String(job?.id || job?.Id || '');

const getJobCompany = (job) => job.requestingCompanyName || job.companyName || '-';

const getJobLocation = (job) => job.workSiteLocation || job.externalWorkSiteName || job.workSiteName || '-';

const getResponseData = (response) => response?.data?.data || response?.data?.Data || response?.data || null;

const getIstanbulDayKey = (value) => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Istanbul'
  }).format(new Date(value));
};

const isTodayInIstanbul = (value) => getIstanbulDayKey(value) === getIstanbulDayKey(new Date());

const isSameIstanbulDay = (value, date) => getIstanbulDayKey(value) === getIstanbulDayKey(date);

const getPriorityMeta = (job) => {
  const raw = String(job?.priority || job?.urgency || job?.importance || '').toLocaleLowerCase('tr-TR');
  if (raw.includes('acil') || raw.includes('urgent')) return { label: 'Acil', className: 'urgent' };
  if (raw.includes('d\u00fc\u015f\u00fck') || raw.includes('dusuk') || raw.includes('low')) return { label: 'D\u00fc\u015f\u00fck', className: 'low' };
  return { label: 'Normal', className: 'normal' };
};

const getNotificationDate = (job) =>
  job?.assignedDate ||
  job?.assignmentDate ||
  job?.assignedAt ||
  job?.updatedDate ||
  job?.createdDate ||
  job?.requestedDate ||
  job?.operationDate;

const mapCalendarProgram = (program) => {
  const id = program?.id ?? program?.Id;
  const title = program?.title ?? program?.Title ?? '';
  const description = program?.description ?? program?.Description ?? '';
  const programDate = program?.programDate ?? program?.ProgramDate ?? new Date().toISOString();

  return {
    id: `manual-program-${id}`,
    calendarProgramId: id,
    subject: title,
    operationDate: programDate,
    requestingCompanyName: 'Manuel Program',
    workSiteLocation: description || '-',
    externalWorkSiteName: description || '-',
    status: 'Assigned',
    isManualProgram: true
  };
};

const getCalendarSlot = (job, index) => {
  if (job.operationDate) {
    const date = new Date(job.operationDate);
    const day = Number.isNaN(date.getDay()) ? index % 7 : (date.getDay() + 6) % 7;
    const hour = Number.isNaN(date.getHours()) ? 8 + (index % 8) : date.getHours();
    return {
      day,
      row: Math.min(Math.max(hour - 7, 1), 10),
      className: ['green', 'blue', 'orange', 'purple'][index % 4]
    };
  }

  return {
    day: index % 7,
    row: 2 + (index % 6),
    className: ['green', 'blue', 'orange', 'purple'][index % 4]
  };
};

const TechnicalServicePersonnelPortalPage = ({ view }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarWeekOffset, setCalendarWeekOffset] = useState(0);
  const [calendarViewMode, setCalendarViewMode] = useState('week');
  const [newProgramOpen, setNewProgramOpen] = useState(false);
  const [newProgramForm, setNewProgramForm] = useState({ subject: '', operationDate: '', description: '' });
  const [extraPrograms, setExtraPrograms] = useState([]);
  const [profileForm, setProfileForm] = useState({ phoneNumber: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profileMessage, setProfileMessage] = useState('');
  const [profileError, setProfileError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const user = useMemo(() => getUserInfoFromToken(), []);

  const fetchJobs = useCallback(async () => {
    const userId = getUserIdFromToken();
    const response = await api.get('/TechnicalService/requests/my', {
      params: userId ? { userId } : {}
    });
    setJobs(response?.data?.data || []);
  }, []);

  const fetchCalendarPrograms = useCallback(async () => {
    const userId = getUserIdFromToken();
    const response = await api.get('/TechnicalService/calendar-programs/my', {
      params: userId ? { userId } : {}
    });
    const data = getResponseData(response);
    setExtraPrograms(Array.isArray(data) ? data.map(mapCalendarProgram) : []);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchJobs(), fetchCalendarPrograms()]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [fetchJobs, fetchCalendarPrograms]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const userId = getUserIdFromToken();
        const response = await api.get('/User/Me');
        const data = response?.data?.data || response?.data || {};
        if (!data?.phoneNumber && userId) {
          const fallback = await api.get(`/User/GetByUserId?userId=${userId}`);
          const fallbackData = fallback?.data?.data || fallback?.data || {};
          setProfileForm({ phoneNumber: fallbackData.phoneNumber || fallbackData.PhoneNumber || '' });
          return;
        }
        setProfileForm({ phoneNumber: data.phoneNumber || data.PhoneNumber || '' });
      } catch (error) {
        setProfileForm({ phoneNumber: localStorage.getItem(`profile_phone_${getUserIdFromToken()}`) || '' });
      }
    };

    if (view === 'profile') {
      loadProfile();
    }
  }, [view]);

  const activeJobs = jobs.filter((job) => normalizeStatus(job.status) < 4);
  const completedJobs = jobs.filter((job) => normalizeStatus(job.status) >= 4);
  const titleMap = {
    dashboard: 'Ana Sayfa',
    calendar: 'Takvim',
    notifications: 'Bildirimler',
    reports: 'Raporlar',
    profile: 'Profil',
    settings: 'Ayarlar'
  };
  const calendarWeekStart = getWeekStart(new Date());
  calendarWeekStart.setDate(calendarWeekStart.getDate() + calendarWeekOffset * 7);
  const calendarWeekDates = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(calendarWeekStart);
    date.setDate(calendarWeekStart.getDate() + index);
    return date;
  });
  const calendarDays = calendarWeekDates.map((date) => {
    const weekday = formatWeekdayShort(date);
    return `${weekday.charAt(0).toLocaleUpperCase('tr-TR')}${weekday.slice(1)} ${date.getDate()}`;
  });
  const calendarHours = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];
  const calendarRangeLabel = `${formatDayMonth(calendarWeekDates[0])} - ${formatDayMonth(calendarWeekDates[6])} ${calendarWeekDates[6].getFullYear()}`;
  const calendarMonthBase = new Date();
  calendarMonthBase.setMonth(calendarMonthBase.getMonth() + calendarWeekOffset);
  const calendarMonthYear = calendarMonthBase.getFullYear();
  const calendarMonth = calendarMonthBase.getMonth();
  const calendarMonthDays = Array.from(
    { length: new Date(calendarMonthYear, calendarMonth + 1, 0).getDate() },
    (_, index) => new Date(calendarMonthYear, calendarMonth, index + 1)
  );
  const calendarSourceItems = [...activeJobs, ...extraPrograms];
  const calendarWeekItems = calendarSourceItems
    .filter((job) => job.operationDate && calendarWeekDates.some((date) => isSameIstanbulDay(job.operationDate, date)))
    .sort((a, b) => new Date(a.operationDate) - new Date(b.operationDate));
  const calendarDayItems = calendarSourceItems
    .filter((job) => job.operationDate && isSameIstanbulDay(job.operationDate, calendarWeekDates[0]))
    .sort((a, b) => new Date(a.operationDate) - new Date(b.operationDate));
  const dayProgramItems = calendarSourceItems
    .filter((job) => isTodayInIstanbul(job.operationDate))
    .sort((a, b) => new Date(a.operationDate) - new Date(b.operationDate));
  const calendarJobs = calendarWeekItems.slice(0, 8).map((job, index) => ({
    ...job,
    slot: getCalendarSlot(job, index)
  }));

  const todayJobs = activeJobs
    .filter((job) => isTodayInIstanbul(job.operationDate))
    .sort((a, b) => new Date(a.operationDate) - new Date(b.operationDate))
    .slice(0, 3);
  const nextJob = activeJobs
    .filter((job) => job.operationDate)
    .slice()
    .sort((a, b) => new Date(a.operationDate) - new Date(b.operationDate))[0];
  const shellNotifications = activeJobs.slice(0, 5).map((job) => {
    const priority = getPriorityMeta(job);
    return {
      id: job.id,
      title: priority.className === 'urgent' ? 'Acil i\u015f atand\u0131' : 'Yeni i\u015f atand\u0131',
      description: `${job.subject} - ${formatDate(job.operationDate)}`
    };
  });

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setSavingProfile(true);
    setProfileMessage('');
    setProfileError('');

    try {
      const userId = getUserIdFromToken();
      const payload = {
        id: userId,
        email: user.email || '',
        phoneNumber: profileForm.phoneNumber || ''
      };
      await api.put('/User/Me', payload);
      localStorage.setItem(`profile_phone_${userId}`, payload.phoneNumber);
      setProfileMessage('Profil bilgileri g\u00fcncellendi.');
    } catch (error) {
      try {
        const userId = getUserIdFromToken();
        await api.put('/User', {
          id: userId,
          email: user.email || '',
          phoneNumber: profileForm.phoneNumber || ''
        });
        localStorage.setItem(`profile_phone_${userId}`, profileForm.phoneNumber || '');
        setProfileMessage('Profil bilgileri g\u00fcncellendi.');
      } catch (fallbackError) {
        setProfileError('Profil bilgileri g\u00fcncellenemedi.');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setSavingPassword(true);
    setProfileMessage('');
    setProfileError('');

    try {
      if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
        throw new Error('EMPTY_PASSWORD');
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        throw new Error('PASSWORD_MISMATCH');
      }
      await api.post('/Auth/ChangePassword', passwordForm);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setProfileMessage('\u015eifre ba\u015far\u0131yla g\u00fcncellendi.');
    } catch (error) {
      setProfileError(
        error?.message === 'PASSWORD_MISMATCH'
          ? 'Yeni \u015fifreler e\u015fle\u015fmiyor.'
          : error?.message === 'EMPTY_PASSWORD'
            ? 'T\u00fcm \u015fifre alanlar\u0131 doldurulmal\u0131.'
            : error?.response?.data?.message || '\u015eifre g\u00fcncellenemedi.'
      );
    } finally {
      setSavingPassword(false);
    }
  };

  const handleNewProgramSubmit = async (event) => {
    event.preventDefault();
    const subject = newProgramForm.subject.trim();
    if (!subject) return;

    const programDate = newProgramForm.operationDate
      ? new Date(newProgramForm.operationDate).toISOString()
      : new Date().toISOString();

    const response = await api.post('/TechnicalService/calendar-programs', {
      title: subject,
      description: newProgramForm.description || '',
      programDate
    });
    const savedProgram = getResponseData(response);

    if (savedProgram) {
      setExtraPrograms((prev) => [...prev, mapCalendarProgram(savedProgram)]);
      setNewProgramOpen(false);
      setNewProgramForm({ subject: '', operationDate: '', description: '' });
    }
  };

  const renderDashboard = () => (
    <div className="service-home-page">
      <section className="service-home-hero">
        <div>
          <h1>{`Merhaba, ${user.name || 'Teknik Servis'}`}</h1>
          <span>{`${activeJobs.length} aktif i\u015f, ${completedJobs.length} tamamlanan i\u015f kayd\u0131 bulunuyor.`}</span>
        </div>
      </section>

      <section className="service-home-stats">
        <div>
          <i className="summary-icon blue"><FaCalendarAlt /></i>
          <span>{'Bug\u00fcnk\u00fc \u0130\u015fler'}</span>
          <strong>{todayJobs.length}</strong>
          <small>Planlanan</small>
        </div>
        <div>
          <i className="summary-icon green"><FaPlay /></i>
          <span>{'Devam Eden \u0130\u015fler'}</span>
          <strong>{activeJobs.filter((job) => normalizeStatus(job.status) === 3).length}</strong>
          <small>{'\u015eu anda'}</small>
        </div>
        <div>
          <i className="summary-icon purple"><FaCheckCircle /></i>
          <span>{'Tamamlanan \u0130\u015fler'}</span>
          <strong>{completedJobs.length}</strong>
          <small>Bu ay</small>
        </div>
        <div>
          <i className="summary-icon orange"><FaRegClock /></i>
          <span>{'Bekleyen \u0130\u015fler'}</span>
          <strong>{activeJobs.length}</strong>
          <small>{'Atanm\u0131\u015f'}</small>
        </div>
      </section>

      <section className="service-home-grid">
        <div className="service-panel service-home-today">
          <div className="service-section-title">
            <div>
              <h2>{'Bug\u00fcnk\u00fc \u0130\u015flerim'}</h2>
              <p>{'Saha ekibinin bug\u00fcn takip edece\u011fi i\u015fler.'}</p>
            </div>
          </div>
          {todayJobs.length > 0 ? (
            todayJobs.map((job, index) => (
              <article key={getJobId(job) || index} className={index === 1 ? 'featured' : ''}>
                <div className="service-home-time">
                  <strong>{formatTime(job.operationDate)}</strong>
                  <span>{formatTime(job.operationDate)}</span>
                </div>
                <i><FaTools /></i>
                <div>
                  <strong>{job.subject}</strong>
                  <span>{getJobCompany(job)}</span>
                  <small><FaMapMarkerAlt /> {getJobLocation(job)}</small>
                </div>
                <em>{normalizeStatus(job.status) === 3 ? 'Devam Ediyor' : 'Planland\u0131'}</em>
              </article>
            ))
          ) : (
            <p className="service-centered-empty">{'Bug\u00fcn i\u00e7in planlanm\u0131\u015f i\u015f bulunmuyor.'}</p>
          )}
        </div>

        <aside className="service-panel service-home-quick">
          <h3>{'H\u0131zl\u0131 Bilgiler'}</h3>
          <div>
            <i className="summary-icon blue"><FaCalendarAlt /></i>
            <span>{'Bug\u00fcnk\u00fc program'}<strong>{`${todayJobs.length} i\u015f`}</strong></span>
            <FaChevronRight />
          </div>
          <div>
            <i className="summary-icon green"><FaRegClock /></i>
            <span>{'Sonraki i\u015f'}<strong>{nextJob ? `${formatTime(nextJob.operationDate)} - ${nextJob.subject}` : '-'}</strong></span>
            <FaChevronRight />
          </div>
          <div>
            <i className="summary-icon orange"><FaMapMarkerAlt /></i>
            <span>{'Sonraki konum'}<strong>{nextJob ? getJobLocation(nextJob) : '-'}</strong></span>
            <FaChevronRight />
          </div>
        </aside>
      </section>
    </div>
  );

  const renderCalendar = () => (
    <div className="service-calendar-page">
      <h1 className="service-calendar-title-line">
        Takvim <span>(Takvim / Program)</span>
      </h1>
      <section className="service-panel service-calendar-panel">
        <div className="service-calendar-header">
          <h2>Takvim</h2>
          <div className="service-calendar-toolbar">
            <button type="button" onClick={() => setCalendarWeekOffset(0)}>{'Bug\u00fcn'}</button>
            <button type="button" aria-label="\u00d6nceki" onClick={() => setCalendarWeekOffset((prev) => prev - 1)}>{'\u2039'}</button>
            <button type="button" aria-label="Sonraki" onClick={() => setCalendarWeekOffset((prev) => prev + 1)}>{'\u203a'}</button>
            <strong>{calendarRangeLabel}</strong>
            <div className="service-calendar-view-toggle">
              <button type="button" className={calendarViewMode === 'day' ? 'active' : ''} onClick={() => setCalendarViewMode('day')}>{'G\u00fcn'}</button>
              <button type="button" className={calendarViewMode === 'week' ? 'active' : ''} onClick={() => setCalendarViewMode('week')}>Hafta</button>
              <button type="button" className={calendarViewMode === 'month' ? 'active' : ''} onClick={() => setCalendarViewMode('month')}>Ay</button>
            </div>
            <button type="button" className="service-calendar-new" onClick={() => setNewProgramOpen(true)}>+ Yeni Program</button>
          </div>
        </div>

        {calendarViewMode === 'week' && (
          <div className="service-week-calendar">
            <div className="service-week-corner" style={{ gridColumn: 1, gridRow: 1 }} />
            {calendarDays.map((day, dayIndex) => (
              <div key={day} className="service-week-day" style={{ gridColumn: dayIndex + 2, gridRow: 1 }}>{day}</div>
            ))}
            {calendarHours.map((hour, hourIndex) => (
              <React.Fragment key={hour}>
                <div className="service-week-hour" style={{ gridColumn: 1, gridRow: hourIndex + 2 }}>{hour}</div>
                {calendarDays.map((day, dayIndex) => (
                  <div key={`${day}-${hour}`} className="service-week-cell" style={{ gridColumn: dayIndex + 2, gridRow: hourIndex + 2 }} />
                ))}
              </React.Fragment>
            ))}
            {calendarJobs.map((job) => (
              <button
                key={job.id}
                type="button"
                className={`service-calendar-event ${job.slot.className}`}
                style={{
                  gridColumn: job.slot.day + 2,
                  gridRow: `${job.slot.row + 1} / span 2`
                }}
              >
                <strong>{job.subject}</strong>
                <span>{`${formatTime(job.operationDate)} - ${job.workSiteLocation || job.externalWorkSiteName || '-'}`}</span>
              </button>
            ))}
          </div>
        )}

        {calendarViewMode === 'day' && (
          <div className="service-calendar-day-view">
            {calendarDayItems.slice(0, 6).map((job) => (
              <article key={getJobId(job)}>
                <strong>{formatTime(job.operationDate)}</strong>
                <div>
                  <h3>{job.subject}</h3>
                  <p>{getJobCompany(job)} - {getJobLocation(job)}</p>
                </div>
                <span>{'Planland\u0131'}</span>
              </article>
            ))}
            {!loading && calendarDayItems.length === 0 && <p className="service-centered-empty">{'Se\u00e7ili g\u00fcn i\u00e7in planlanm\u0131\u015f i\u015f bulunmuyor.'}</p>}
          </div>
        )}

        {calendarViewMode === 'month' && (
          <div className="service-calendar-month-view">
            {calendarMonthDays.map((day) => {
              const dayPrograms = calendarSourceItems.filter((job) => {
                return job.operationDate && isSameIstanbulDay(job.operationDate, day);
              });
              return (
                <article key={day.toISOString()}>
                  <strong>{day.getDate()}</strong>
                  {dayPrograms.slice(0, 2).map((job) => (
                    <span key={getJobId(job)}>{job.subject}</span>
                  ))}
                </article>
              );
            })}
          </div>
        )}

        <div className="service-program-list">
          <h3>{'G\u00fcn\u00fcn Program\u0131'}</h3>
          {dayProgramItems.slice(0, 4).map((job) => (
            <div key={job.id}>
              <span>{formatTime(job.operationDate)}</span>
              <strong>{job.subject}</strong>
              <small>{job.workSiteLocation || job.externalWorkSiteName || '-'}</small>
              <em>{'Planland\u0131'}</em>
              <button type="button">Detay</button>
            </div>
          ))}
          {!loading && dayProgramItems.length === 0 && (
            <p className="service-centered-empty">{'Bug\u00fcn i\u00e7in planlanm\u0131\u015f i\u015f bulunmuyor.'}</p>
          )}
        </div>

        {newProgramOpen && (
          <div className="service-inline-modal">
            <form onSubmit={handleNewProgramSubmit}>
              <div className="service-section-title">
                <div>
                  <h2>Yeni Program</h2>
                  <p>{'Takvim i\u00e7in ge\u00e7ici program notu olu\u015ftur.'}</p>
                </div>
              </div>
              <label>{'Program Ba\u015fl\u0131\u011f\u0131'}<input value={newProgramForm.subject} onChange={(event) => setNewProgramForm((prev) => ({ ...prev, subject: event.target.value }))} /></label>
              <label>Tarih<input type="datetime-local" value={newProgramForm.operationDate} onChange={(event) => setNewProgramForm((prev) => ({ ...prev, operationDate: event.target.value }))} /></label>
              <label>{'A\u00e7\u0131klama'}<input value={newProgramForm.description} onChange={(event) => setNewProgramForm((prev) => ({ ...prev, description: event.target.value }))} /></label>
              <div>
                <button type="button" onClick={() => setNewProgramOpen(false)}>{'Vazge\u00e7'}</button>
                <button type="submit">Kaydet</button>
              </div>
            </form>
          </div>
        )}
      </section>
    </div>
  );

  const renderNotifications = () => (
    <section className="service-panel service-notifications-panel">
      <div className="service-section-title centered-title">
        <div>
          <h2>Bildirimler</h2>
          <p>{'Saha i\u015fleriyle ilgili sistem bildirimleri.'}</p>
        </div>
      </div>
      <div className="service-notification-list">
        {activeJobs.map((job) => {
          const priority = getPriorityMeta(job);
          return (
          <div key={job.id} className="service-notification-card">
            <span className={priority.className === 'urgent' ? 'danger' : 'info'}>
              {priority.className === 'urgent' ? <FaBell /> : <FaTools />}
            </span>
            <div>
              <strong>{priority.className === 'urgent' ? 'Acil i\u015f atand\u0131' : 'Yeni i\u015f atand\u0131'}</strong>
              <p>{`${job.subject} i\u015fi size atand\u0131. Talep No: ${job.requestNo || job.id}`}</p>
              <div className="service-notification-meta">
                <em>{`Firma: ${getJobCompany(job)}`}</em>
                <em>{`Konum: ${getJobLocation(job)}`}</em>
                <em>{`Planlanan: ${formatDate(job.operationDate)}`}</em>
                <em>{`Öncelik: ${priority.label}`}</em>
              </div>
            </div>
            <small>{formatDate(getNotificationDate(job))}</small>
          </div>
        );
        })}
        {!loading && activeJobs.length === 0 && <p>{'G\u00f6sterilecek bildirim bulunmuyor.'}</p>}
      </div>
    </section>
  );

  const renderReports = () => (
    <section className="service-panel service-reports-panel visual">
      <div className="service-section-title centered-title">
        <div>
          <h2>Raporlar</h2>
          <p>{'Ki\u015fisel saha i\u015flerinin k\u0131sa \u00f6zeti.'}</p>
        </div>
      </div>
      <div className="service-report-stats">
        <div><span>{'Toplam \u0130\u015f'}</span><strong>{jobs.length}</strong></div>
        <div><span>{'Tamamlanan \u0130\u015f'}</span><strong>{completedJobs.length}</strong></div>
        <div><span>{'Devam Eden \u0130\u015f'}</span><strong>{activeJobs.length}</strong></div>
      </div>
      <div className="service-report-visual">
        <div>
          <span>{'Tamamlanma Oran\u0131'}</span>
          <strong>{jobs.length ? Math.round((completedJobs.length / jobs.length) * 100) : 0}%</strong>
          <small>{`${completedJobs.length} / ${jobs.length} i\u015f`}</small>
        </div>
        <p>{'Ayl\u0131k saha performans\u0131, aktif i\u015f y\u00fck\u00fc ve tamamlanan servis formlar\u0131 buradan takip edilir.'}</p>
      </div>
      <div className="service-report-table">
        {[
          'Ayl\u0131k \u0130\u015f \u00d6zeti',
          'Teknik Servis Detay Raporu',
          'Tamamlanan \u0130\u015fler Raporu'
        ].map((name) => (
          <div key={name}>
            <span>{name}</span>
            <small>{formatDate(new Date())}</small>
            <button type="button"><FaDownload /></button>
          </div>
        ))}
      </div>
    </section>
  );

  const renderProfile = () => (
    <section className="service-panel service-profile-panel">
      <div className="service-section-title">
        <div>
          <h2>Profil</h2>
          <p>{'Saha ekibi hesab\u0131 ve ki\u015fisel bilgiler.'}</p>
        </div>
      </div>
      <div className="service-profile-grid">
        <div className="service-profile-summary">
          <FaUserCircle />
          <h3>{user.name || 'Teknik Servis'}</h3>
          <p>{user.email || '-'}</p>
          <span>Saha Ekibi</span>
        </div>
        <div className="service-settings-grid embedded">
          <form onSubmit={handleProfileSubmit}>
            <h3>Profil Bilgileri</h3>
            <label>Ad Soyad<input value={user.name || ''} readOnly /></label>
            <label>E-posta<input value={user.email || ''} readOnly /></label>
            <label>Telefon<input value={profileForm.phoneNumber} onChange={(event) => setProfileForm((prev) => ({ ...prev, phoneNumber: event.target.value }))} placeholder="Telefon" /></label>
            <button type="submit" className="service-primary-action" disabled={savingProfile}>
              {savingProfile ? 'Kaydediliyor...' : 'Bilgileri Kaydet'}
            </button>
          </form>
          <form onSubmit={handlePasswordSubmit}>
            <h3>{'\u015eifre De\u011fi\u015ftir'}</h3>
            <label>{'Mevcut \u015eifre'}<input type="password" value={passwordForm.currentPassword} onChange={(event) => setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))} /></label>
            <label>{'Yeni \u015eifre'}<input type="password" value={passwordForm.newPassword} onChange={(event) => setPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))} /></label>
            <label>{'Yeni \u015eifre Tekrar'}<input type="password" value={passwordForm.confirmPassword} onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))} /></label>
            <button type="submit" className="service-primary-action" disabled={savingPassword}>
              {savingPassword ? 'G\u00fcncelleniyor...' : '\u015eifreyi G\u00fcncelle'}
            </button>
          </form>
        </div>
      </div>
      {profileMessage && <div className="technical-service-alert success">{profileMessage}</div>}
      {profileError && <div className="technical-service-alert error">{profileError}</div>}
    </section>
  );

  const renderSettings = () => (
    <section className="service-panel service-settings-panel">
      <div className="service-section-title">
        <div>
          <h2>Ayarlar</h2>
          <p>{'Profil ve saha hesab\u0131 bilgileri.'}</p>
        </div>
      </div>
      <div className="service-settings-grid">
        <div>
          <h3>Profil Bilgileri</h3>
          <label>Ad Soyad<input value={user.name || ''} readOnly /></label>
          <label>E-posta<input value={user.email || ''} readOnly /></label>
          <label>Ekip<input value="Saha Ekibi" readOnly /></label>
        </div>
        <div>
          <h3>Hesap Bilgileri</h3>
          <p><strong>Kullan\u0131c\u0131 Rol\u00fc</strong><span>Saha Ekibi</span></p>
          <p><strong>Hesap Durumu</strong><span className="active">Aktif</span></p>
        </div>
      </div>
    </section>
  );

  return (
    <TechnicalServicePersonnelShell
      title={titleMap[view]}
      notificationCount={activeJobs.length}
      notifications={shellNotifications}
      hideTopbar={['dashboard', 'calendar', 'notifications', 'reports', 'profile'].includes(view)}
      shellClassName={view === 'calendar' ? 'calendar-reference-shell' : ''}
    >
      {view === 'dashboard' && renderDashboard()}
      {view === 'calendar' && renderCalendar()}
      {view === 'notifications' && renderNotifications()}
      {view === 'reports' && renderReports()}
      {view === 'profile' && renderProfile()}
      {view === 'settings' && renderSettings()}
    </TechnicalServicePersonnelShell>
  );
};

export default TechnicalServicePersonnelPortalPage;
